
import { computed, onMounted, ref, watch } from 'vue';
import IftaLabel from 'primevue/iftalabel';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Password from 'primevue/password';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import InputOtp from 'primevue/inputotp';
import ProgressSpinner from 'primevue/progressspinner';
import Checkbox from 'primevue/checkbox';
import CheckboxGroup from 'primevue/checkboxgroup';
import Message from 'primevue/message';
import Toast from 'primevue/toast';

import { showVuePopup } from '../utils/showVuePopup';
import ForgotPassword from './ForgotPassword.vue';
import { external } from '../apis/external';
import { wp } from '../apis/wp';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useWebStore } from '../stores/web';
import { useAuthStore } from '../stores/auth';

interface Options {
	marketingRefId: string;
	affiliationRefId: string;
}

interface RecommendOption {
	name: string;
	value: string;
}

export default {
	name: 'Register',
	components: {
		Toast,
		Message,
		IftaLabel,
		InputText,
		InputMask,
		Password,
		Select,
		Divider,
		InputOtp,
		Checkbox,
		CheckboxGroup,
		ProgressSpinner,
		Button,
	},
	props: {
		close: Function,
		options: {
			type: Object as () => Options,
			required: false,
			default: () => ({ marketingRefId: '', affiliationRefId: '' }),
		},
	},
	setup(props) {
		const auth = useAuthStore();

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();

		const recommends = ref<RecommendOption[]>([]);
		const loading = ref(false);

		// ทำให้ reactive ต่อ props จริง ๆ
		const mkId = ref<string>((props.options.marketingRefId ?? '').trim());
		const afId = ref<string>((props.options.affiliationRefId ?? '').trim());

		const form = ref({
			mode: 2,
			phone: { value: '', invalid: false, msgErr: '' },
			password: { value: '', invalid: false, msgErr: '' },
			otp: { ref: '', expired: '', code: '' },
			captcha: { recId: '', image: '', code: '' },
			recommend: { value: 'Google', invalid: false, msgErr: '' },
			recommendOther: { value: '', invalid: false, msgErr: '' },
			btn: { disabled: false, loading: false },
		});

		const hasRefId = computed(() => {
			const m = (mkId.value ?? '').trim();
			const a = (afId.value ?? '').trim();
			return m !== '' || a !== '';
		});

		const btnSubmitDisabled = computed(() => {
			const phoneValid = form.value.phone.value.length === 10;
			const passLen = form.value.password.value.length;
			const passValid = passLen >= 6 && passLen <= 30;
			return phoneValid && passValid;
		});

		const findOptionValue = (val: string) =>
			recommends.value.find(o => o.value === val)?.value ?? '';

		function syncRecommendByProps() {
			// ถ้ารายการยังไม่มา ให้รอ
			if (!recommends.value || recommends.value.length === 0) return;

			const hasAff = (afId.value ?? '').trim() !== '';
			const hasMkt = (mkId.value ?? '').trim() !== '';

			if (hasAff) {
				// มี affiliationRefId → บังคับเป็น "เพื่อนแนะนำ"
				const forced = findOptionValue('เพื่อนแนะนำ') || 'เพื่อนแนะนำ';
				form.value.recommend.value = forced;
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			} else if (hasMkt) {
				// มี marketingRefId → บังคับเป็น "โฆษณาอื่น ๆ"
				const forced = findOptionValue('โฆษณาอื่น ๆ') || 'โฆษณาอื่น ๆ';
				form.value.recommend.value = forced;
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			} else {
				// ไม่มี refId → default เป็น "Google"
				const def = findOptionValue('Google') || 'Google';
				if (!form.value.recommend.value) {
					form.value.recommend.value = def;
				}
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			}
		}

		const handleRecommendList = async () => {
			try {
				const resp = await external.recommendList({ data: null });
				if (resp.code !== '0') throw new Error(resp.message);
				// สมมติ backend ส่งรูปแบบ { name, value } อยู่แล้ว
				recommends.value = resp.result?.recommend || [];
				// sync หลังได้รายการ
				syncRecommendByProps();
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleWpRegisterMode = async () => {
			try {
				const resp = await wp.optionKey('opt-switch-register-mode');
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.mode = parseInt(resp.result);
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleCaptcha = async () => {
			try {
				const resp = await external.captcha({ data: null });
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.captcha = resp.result;
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const clickRefreshCaptcha = async () => {
			form.value.captcha = { image: '', recId: '', code: '' };
			await handleCaptcha();
		};

		const handleOTP = async () => {
			try {
				const resp = await external.otp({ phone: form.value.phone.value });
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.otp = resp.result;
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'OTP ล้มเหลว',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleSubmit = async () => {
			form.value.btn.loading = true;
			form.value.phone.invalid = false;
			form.value.password.invalid = false;

			const phone = form.value.phone.value;
			const password = form.value.password.value;

			if (phone === '' || phone.length !== 10) {
				form.value.phone.invalid = true;
				form.value.phone.msgErr = 'เบอร์โทรศัพท์ไม่ถูกต้อง';
				form.value.btn.loading = false;
				return;
			}

			if (password.length < 6 || password.length > 30) {
				form.value.password.invalid = true;
				form.value.password.msgErr = 'รหัสผ่านต้องมี 6-30 ตัวอักษร';
				form.value.btn.loading = false;
				return;
			}

			try {
				const payload = {
					username: phone,
					password: password,
					tel: phone,
					firstName: '',
					lastName: '',
					middleName: '',
					email: '',
					verifyType: form.value.mode,
					verifyRef1:
						form.value.mode === 1 ? form.value.otp.ref : form.value.captcha.recId,
					verifyRef2:
						form.value.mode === 1 ? form.value.otp.code : form.value.captcha.code,
					recommend: form.value.recommend.value,
					recommendOther: form.value.recommendOther.value,
					refId: afId.value,               // ใช้ค่าที่ sync แล้ว
					affBrand: '',
					marketingId: mkId.value,         // ใช้ค่าที่ sync แล้ว
					lineId: '',
					isTelegram: false,
					isCheckUsername: false,
					isNoRequirePhone: false,
				};

				if (payload.verifyRef1 === '' || payload.verifyRef2 === '') {
					throw new Error('กรุณากรอกข้อมูลยืนยันให้ครบถ้วน');
				}

				const resp = await external.register(payload);
				if (resp.code !== '0') throw new Error(resp.message);

				toast.add({
					severity: 'success',
					summary: 'สำเร็จ',
					detail: 'สมัครสมาชิกเรียบร้อย',
					life: 3000,
				});

				try {
					await auth.login({
						username: resp.result.username,
						password: resp.result.password,
					});

					window.location.href = '/slot';
				} catch (error) {
					toast.add({
						severity: 'error',
						summary: 'เกิดข้อผิดพลาด',
						detail: error,
						life: 3000,
					});
				}

				props.close?.();
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error,
					life: 3000,
				});
			} finally {
				form.value.btn.loading = false;
			}
		};

		const clickBtnForgotPassword = () => {
			showVuePopup(ForgotPassword);
		};

		const checkPhoneNumber = () => {
			const phone = form.value.phone.value.trim();
			const isValid = /^0[0-9]{9}$/.test(phone);
			if (!isValid) {
				form.value.phone.invalid = true;
				form.value.phone.msgErr = 'เบอร์โทรศัพท์ไม่ถูกต้อง ต้องเป็นตัวเลข 10 หลัก';
				return;
			}
			form.value.phone.invalid = false;
			form.value.phone.msgErr = '';
		};

		const checkPassword = () => {
			const password = form.value.password.value.trim();

			// if (!/^[A-Z]/.test(password)) {
			// 	form.value.password.invalid = true;
			// 	form.value.password.msgErr = 'รหัสผ่านต้องขึ้นต้นด้วยตัวพิมพ์ใหญ่';
			// 	return;
			// }

			// if (!/[a-z]/.test(password)) {
			// 	form.value.password.invalid = true;
			// 	form.value.password.msgErr = 'รหัสผ่านต้องมีตัวพิมพ์เล็กอย่างน้อย 1 ตัว';
			// 	return;
			// }
			//
			// if (!/[0-9]/.test(password)) {
			// 	form.value.password.invalid = true;
			// 	form.value.password.msgErr = 'รหัสผ่านต้องมีตัวเลขอย่างน้อย 1 ตัว';
			// 	return;
			// }

			const allowedChars = /^[A-Za-z0-9@#$%&*!_.?\-]+$/;
			if (!allowedChars.test(password)) {
				form.value.password.invalid = true;
				form.value.password.msgErr =
					'รหัสผ่านใช้ได้เฉพาะ A-Z, a-z, 0-9 และ @#$%&*!_.?-';
				return;
			}

			if (password.length < 8) {
				form.value.password.invalid = true;
				form.value.password.msgErr = 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร';
				return;
			}

			form.value.password.invalid = false;
			form.value.password.msgErr = '';
		};

		// --- Lifecycle / watchers ---
		onMounted(async () => {
			await handleWpRegisterMode();
			if (form.value.mode === 2) await handleCaptcha();
			await handleRecommendList(); // ภายในจะเรียก syncRecommendByProps แล้ว
		});

		// อัปเดต mkId/afId เมื่อ props เปลี่ยน แล้วค่อย sync
		watch(
			() => props.options,
			(v) => {
				mkId.value = (v?.marketingRefId ?? '').trim();
				afId.value = (v?.affiliationRefId ?? '').trim();
				syncRecommendByProps();
			},
			{ deep: true }
		);

		// ถ้า list เปลี่ยน (เช่นโหลดช้ากว่า) → sync อีกครั้ง
		watch(
			() => recommends.value,
			() => syncRecommendByProps()
		);

		// ถ้าผู้ใช้เลือก "อื่นๆ" → เปิดช่องกรอกข้อความ
		watch(
			() => form.value.recommend.value,
			(val) => {
				if (val === 'อื่นๆ') {
					form.value.recommendOther.invalid = false;
				} else {
					form.value.recommendOther.value = '';
					form.value.recommendOther.invalid = false;
					form.value.recommendOther.msgErr = '';
				}
			}
		);

		return {
			form,
			recommends,
			loading,
			hasRefId,
			handleSubmit,
			clickBtnForgotPassword,
			clickRefreshCaptcha,
			btnSubmitDisabled,
			checkPhoneNumber,
			checkPassword,
			handleOTP,
		};
	},
};
